import type { AnalyticsConfig, AppConfig, RumConfig } from '@/types';

interface FullConfig extends AppConfig {
  rum?: RumConfig;
  analytics?: AnalyticsConfig;
}

let cachedConfig: FullConfig | null = null;

export async function loadConfig(): Promise<FullConfig> {
  if (cachedConfig) return cachedConfig;

  const response = await fetch('/config.json');
  // Vite's dev server serves the SPA shell (text/html) for unknown routes
  // like /config.json when no public/config.json exists — so a 200 status
  // alone isn't enough; check Content-Type before trying to parse JSON.
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  if (!response.ok || !isJson) {
    // The body is not used, so release it: an unread fetch body keeps the
    // request open for the life of the page (the SPA-shell fallback that
    // nginx and Vite serve for a missing config.json), which also means the
    // page never reaches network idle.
    await response.body?.cancel().catch(() => undefined);
    // Fall back to build-time env vars
    cachedConfig = {
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
      cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
      cognitoRegion: import.meta.env.VITE_COGNITO_REGION ?? 'us-east-1',
      environment: import.meta.env.VITE_ENVIRONMENT ?? 'dev',
      version: '0.0.0',
      // IC-194: never fabricate a build timestamp — an empty string renders as
      // "unknown" in the About dialog instead of masquerading as a build date.
      buildTimestamp: '',
    };
    return cachedConfig;
  }

  const json = await response.json();
  cachedConfig = {
    apiBaseUrl: json.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '',
    cognitoUserPoolId: json.cognitoUserPoolId ?? import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
    cognitoClientId: json.cognitoClientId ?? import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
    cognitoRegion: json.cognitoRegion ?? import.meta.env.VITE_COGNITO_REGION ?? 'us-east-1',
    environment: json.environment ?? import.meta.env.VITE_ENVIRONMENT ?? 'dev',
    version: json.version ?? '0.0.0',
    buildTimestamp: json.buildTimestamp ?? '',
    rum: json.rum,
    analytics: json.analytics,
  };

  return cachedConfig;
}

export function getConfig(): FullConfig {
  if (!cachedConfig) {
    throw new Error('Config not loaded. Call loadConfig() first.');
  }
  return cachedConfig;
}
